import React from "react";
import _ from "lodash";
import { Page, Text, View, Image } from "@react-pdf/renderer";

import { ADDRESS_TYPES, RELATIONSHIP } from "../../../constants/constants";
import CDK_Logo from "../../../images/CDKGlobalWM_BLK.jpg";
import commonStyles from "../PDFStyles/common";
import styles from "../PDFStyles/Form2";
import {
  decrypt,
  getAddressAsString,
  getFormattedDate,
  getFullName,
  getGenderName,
  getMaritalName
} from "../../../util/helpers";
import {
  getAddressValue,
  getDeclarationPlace,
  getFatherOrMotherOrSpouseName,
  getNomineeData,
  getRelationDetails,
  getRelationShipName
} from "../../../util/onBoardingDataFilters";

const Form2 = React.memo(
  ({
    candidateDetails,
    genders,
    maritalStatusList,
    relationShipList,
    states
  }) => {
    const {
      accountNumber,
      addresses,
      birthDate,
      dateofJoining,
      declarationDate,
      declarationPlace,
      ePFNomineeDetails,
      familyMembers=[],
      gender,
      gratuityIsFatherOrMotherOrSpouse,
      maritalStatus
    } = candidateDetails

    const nomineeIds = _.map(
      ePFNomineeDetails,
      "familyMemberId"
    );

    const getNominees = () =>
      familyMembers.filter(member =>
        nomineeIds.includes(member.id)
      );

    const getRelationObj = id => {
      const relationObj = getRelationDetails(
        id,
        familyMembers
      );
      return relationObj.isEPSNominee ? relationObj : null;
    };

    const father = getRelationObj(RELATIONSHIP.FATHER.id);
    const mother = getRelationObj(RELATIONSHIP.MOTHER.id);
    const spouse = getRelationObj(RELATIONSHIP.HUSBAND.id);
    const fullName = getFullName(candidateDetails);
    const currentCity = getAddressValue(
      "city",
      addresses,
      ADDRESS_TYPES.CURRENT
    );

    const fatherOrMotherOrSpouseName = getFatherOrMotherOrSpouseName(
      gratuityIsFatherOrMotherOrSpouse,
      familyMembers
    );

    return (
      <Page size="A4" style={commonStyles.page}>
        <Image style={commonStyles.cdkLogo} src={CDK_Logo} />
        <View style={commonStyles.header}>
          <Text style={commonStyles.headerTitleWithUnderline}>
            FORM-2 (Revised)
          </Text>
          <View>
            <Text>
              NOMINATION AND DECLARATIONFORM FOR UNEXEMPTED/ EXEMPTED
              ESTABLISHMENT
            </Text>
            <Text>
              Declaration and Nomination Form under the Employee Provident Funds
              & Employee Pension Scheme
            </Text>
          </View>
          <Text style={styles.headerParagraph}>
            (Paragraph 33 & 61 (1) of the Employee Provident Fund Scheme, 1952 &
            Paragraph 18 of the Employee’s Pension Scheme, 1995)
          </Text>
        </View>

        <View style={commonStyles.marginTop10}>
          <View style={styles.employeeDetails}>
            <Text style={styles.employeeDetailsKey}>
              1. Name (in Block Letters)
            </Text>
            <Text
              style={styles.employeeDetailsValue}
            >{`:  ${fullName.toUpperCase()}`}</Text>
          </View>
          <View style={styles.employeeDetails}>
            <Text style={styles.employeeDetailsKey}>
              2. Father’s/Mother's/Spouse’sName
            </Text>
            <Text style={styles.employeeDetailsValue}>
              {`:  ${fatherOrMotherOrSpouseName}`}
            </Text>
          </View>
          <View style={styles.employeeDetails}>
            <Text style={styles.employeeDetailsKey}>3. Date of birth</Text>
            <Text style={styles.employeeDetailsValue}>
              {`:  ${getFormattedDate(birthDate)}`}
            </Text>
          </View>
          <View style={styles.employeeDetails}>
            <Text style={styles.employeeDetailsKey}>4. Sex</Text>
            <Text style={styles.employeeDetailsValue}>
              {`:  ${getGenderName(gender, genders)}`}
            </Text>
          </View>
          <View style={styles.employeeDetails}>
            <Text style={styles.employeeDetailsKey}>5. Marital Status</Text>
            <Text style={styles.employeeDetailsValue}>
              {`:  ${getMaritalName(
                maritalStatus,
                maritalStatusList,
                true
              )}`}
            </Text>
          </View>
          <View style={styles.employeeDetails}>
            <Text style={styles.employeeDetailsKey}>6. Account No</Text>
            <Text style={styles.employeeDetailsValue}>
              {`:  `}
            </Text>
          </View>
          <View style={styles.employeeDetails}>
            <Text style={styles.employeeDetailsKey}>
              7. Address - Permanent
            </Text>
            <Text style={styles.employeeDetailsValue}>
              {`:  ${getAddressAsString(
                ADDRESS_TYPES.PERMANENT,
                addresses,
                states
              )}`}
            </Text>
          </View>
          <View style={styles.employeeDetails}>
            <Text
              style={{ ...styles.employeeDetailsKey, ...styles.paddingLeft55 }}
            >
              Current
            </Text>
            <Text style={styles.employeeDetailsValue}>
              {`:  ${getAddressAsString(
                ADDRESS_TYPES.CURRENT,
                addresses,
                states
              )}`}
            </Text>
          </View>
          <View style={styles.employeeDetails}>
            <Text style={styles.employeeDetailsKey}>8. Date of Joining</Text>
            <Text style={styles.employeeDetailsValue}>
              {`:  ${getFormattedDate(dateofJoining)}`}
            </Text>
          </View>
        </View>

        <Text style={commonStyles.header}>PART – A (EPF)</Text>

        <Text>
          I hereby nominate the person(s)/cancel the nomination made by me
          previously and nominate the person(s) mentioned below to receive the
          amount standing to my credit in {"the "}
          <Text style={commonStyles.fontBold}>Employee’s Provident Fund</Text>,
          in the event of Death.
        </Text>

        <View style={styles.nomineesTable}>
          <View style={{ ...commonStyles.tableRow, ...commonStyles.fontBold }}>
            <View
              style={{ ...commonStyles.tableCol, ...commonStyles.textCenter }}
            >
              <Text style={commonStyles.tableCell}>
                Name of the Nominee / Nominees
              </Text>
            </View>
            <View style={{ ...commonStyles.tableCol }}>
              <Text style={commonStyles.tableCell}>Address</Text>
            </View>
            <View style={styles.epfNomineesRelationship}>
              <Text style={commonStyles.tableCell}>
                Nominees’s relationship with the Member
              </Text>
            </View>
            <View style={styles.tableColWithWidth15}>
              <Text style={commonStyles.tableCell}>Date of Birth</Text>
            </View>
            <View style={styles.epfAmountShare}>
              <Text style={commonStyles.tableCell}>
                Total amount of Share of accumulations in provident fund to be
                paid to each nominee
              </Text>
            </View>
            <View style={styles.epfGuardianDetails}>
              <Text style={commonStyles.tableCell}>
                If the Nominee is a minor name & relationship & address of the
                guardian who may receive the amount during the minority of
                nominee
              </Text>
            </View>
          </View>
          {getNominees().map(epfNomination => (
            <View style={commonStyles.tableRow}>
              <View style={commonStyles.tableCol}>
                <Text style={commonStyles.tableCell}>
                  {epfNomination.fullName}
                </Text>
              </View>
              <View style={commonStyles.tableCol}>
                <Text style={commonStyles.tableCell}>{currentCity}</Text>
              </View>
              <View style={styles.tableColWithWidth15}>
                <Text style={commonStyles.tableCell}>
                  {getRelationShipName(
                    epfNomination.relationShipType,
                    relationShipList
                  )}
                </Text>
              </View>
              <View style={styles.tableColWithWidth15}>
                <Text style={commonStyles.tableCell}>
                  {epfNomination.dateOfBirth &&
                    epfNomination.dateOfBirth.split("T")[0]}
                </Text>
              </View>
              <View
                style={{ ...commonStyles.tableCol, ...commonStyles.width10 }}
              >
                <Text style={commonStyles.tableCell}>
                  {getNomineeData(
                    ePFNomineeDetails,
                    "ePFSharePercentage",
                    epfNomination.id
                  )}
                </Text>
              </View>
              <View
                style={{ ...commonStyles.tableCol, ...commonStyles.width20 }}
              >
                <Text style={commonStyles.tableCell}>
                  {getNomineeData(
                    ePFNomineeDetails,
                    "guradianName",
                    epfNomination.id
                  )}
                </Text>
              </View>
            </View>
          ))}
        </View>

        <Text style={commonStyles.marginTop10}>
          1. Certified that I have no family as defined in para 2(g) of the
          Employees’ Provident Fund Scheme, 1952 and should I acquire a family
          hereafter the above nomination should be deemed or cancelled.
        </Text>

        <Text>
          2. Certified that my father/mother is/are dependent upon me.
        </Text>

        <View style={styles.subscriberSignature}>
          <Text style={commonStyles.textBlack}>{fullName}</Text>
          <Text style={commonStyles.textRed}>
            Signature or Thumb Impression of the Subscriber
          </Text>
        </View>

        <Image break style={commonStyles.cdkLogo} src={CDK_Logo} />
        <Text style={styles.partsBHeader}>Part – B (EPS) (Para 19)</Text>

        <Text>
          I hereby furnish below particulars of the members of my family who
          would be eligible or receive Widow/Children pension in the event of my
          death.
        </Text>

        <View style={commonStyles.table}>
          <View style={{ ...commonStyles.tableRow, ...commonStyles.fontBold }}>
            <View style={{ ...commonStyles.tableCol, ...commonStyles.width10 }}>
              <Text style={commonStyles.tableCell}>Sl.No.</Text>
            </View>
            <View style={{ ...commonStyles.tableCol, ...commonStyles.width30 }}>
              <Text style={commonStyles.tableCell}>
                Name of the family member
              </Text>
            </View>
            <View style={{ ...commonStyles.tableCol, ...commonStyles.width30 }}>
              <Text style={commonStyles.tableCell}>
                Address of the family member
              </Text>
            </View>
            <View style={styles.epfDOB}>
              <Text style={commonStyles.tableCell}>Date of Birth</Text>
            </View>
            <View style={styles.gratuityProportion}>
              <Text
                style={{
                  ...commonStyles.tableCell,
                  ...commonStyles.textCenter
                }}
              >
                Relationship with the member
              </Text>
            </View>
          </View>
          <View style={commonStyles.tableRow}>
            <View style={{ ...commonStyles.tableCol, ...commonStyles.width10 }}>
              <Text style={commonStyles.tableCell}>1</Text>
            </View>
            <View style={{ ...commonStyles.tableCol, ...commonStyles.width30 }}>
              <Text style={commonStyles.tableCell}>2</Text>
            </View>
            <View style={{ ...commonStyles.tableCol, ...commonStyles.width30 }}>
              <Text style={commonStyles.tableCell}>3</Text>
            </View>
            <View style={styles.epfDOB}>
              <Text style={commonStyles.tableCell}>4</Text>
            </View>
            <View style={styles.gratuityProportion}>
              <Text style={{ ...commonStyles.tableCell }}>5</Text>
            </View>
          </View>
          {spouse && (
            <View style={commonStyles.tableRow}>
              <View
                style={{ ...commonStyles.tableCol, ...commonStyles.width10 }}
              >
                <Text style={commonStyles.tableCell}>{1}</Text>
              </View>
              <View
                style={{ ...commonStyles.tableCol, ...commonStyles.width30 }}
              >
                <Text style={commonStyles.tableCell}>{spouse.fullName}</Text>
              </View>
              <View
                style={{ ...commonStyles.tableCol, ...commonStyles.width30 }}
              >
                <Text style={commonStyles.tableCell}>{currentCity}</Text>
              </View>
              <View style={styles.epfDOB}>
                <Text style={commonStyles.tableCell}>
                  {spouse.dateOfBirth.split("T")[0]}
                </Text>
              </View>
              <View style={styles.gratuityProportion}>
                <Text style={{ ...commonStyles.tableCell }}>
                  {getRelationShipName(
                    spouse.relationShipType,
                    relationShipList
                  )}
                </Text>
              </View>
            </View>
          )}
        </View>

        <Text style={styles.nomineeCertifiedStatement}>
          Certified that I have no family as defined in para 2 (vii) of
          Employee’s Pension scheme, 1995 and should I acquire a family
          hereafter I shall furnish particulars thereon in the above form.
        </Text>

        <Text>
          I hereby nominate the following person for receiving the monthly widow
          pension (admissible under para 16(2) a (i) & (ii) in the event without
          leaving any eligible member for receiving pension.
        </Text>

        <View style={styles.nomineeTable}>
          <View style={{ ...commonStyles.tableRow, ...commonStyles.fontBold }}>
            <View style={styles.nameAndAddressWidth}>
              <Text style={commonStyles.tableCell}>
                Name and address of the Nominee
              </Text>
            </View>
            <View style={styles.tableColWithWidth15}>
              <Text style={commonStyles.tableCell}>Date of Birth</Text>
            </View>
            <View style={styles.relationShipWidth}>
              <Text style={commonStyles.tableCell}>
                Relationship with the member
              </Text>
            </View>
          </View>
          <View style={commonStyles.tableRow}>
            <View style={styles.nameAndAddressWidth}>
              <Text style={commonStyles.tableCell}>1</Text>
            </View>
            <View style={styles.tableColWithWidth15}>
              <Text style={commonStyles.tableCell}>2</Text>
            </View>
            <View style={styles.relationShipWidth}>
              <Text style={commonStyles.tableCell}>3</Text>
            </View>
          </View>
          {father && (
            <View style={commonStyles.tableRow}>
              <View style={styles.nameAndAddressWidth}>
                <Text style={commonStyles.tableCell}>
                  {`${father.fullName}, ${currentCity}`}
                </Text>
              </View>
              <View style={styles.tableColWithWidth15}>
                <Text style={commonStyles.tableCell}>
                  {father.dateOfBirth.split("T")[0]}
                </Text>
              </View>
              <View style={styles.relationShipWidth}>
                <Text style={commonStyles.tableCell}>
                  {getRelationShipName(
                    RELATIONSHIP.FATHER.id,
                    relationShipList
                  )}
                </Text>
              </View>
            </View>
          )}
          {mother && (
            <View style={commonStyles.tableRow}>
              <View style={styles.nameAndAddressWidth}>
                <Text style={commonStyles.tableCell}>
                  {mother.fullName + currentCity}
                </Text>
              </View>
              <View style={styles.tableColWithWidth15}>
                <Text style={commonStyles.tableCell}>
                  {mother.dateOfBirth.split("T")[0]}
                </Text>
              </View>
              <View style={styles.relationShipWidth}>
                <Text style={commonStyles.tableCell}>
                  {getRelationShipName(
                    RELATIONSHIP.MOTHER.id,
                    relationShipList
                  )}
                </Text>
              </View>
            </View>
          )}
        </View>

        <View
          style={{
            ...commonStyles.flexRowSpaceBetween,
            ...commonStyles.textRed
          }}
        >
          <View style={styles.form2DeclarationDate}>
            <Text>
              Date :
              <Text style={commonStyles.textBlack}>
                {getFormattedDate(declarationDate)}
              </Text>
            </Text>
          </View>
          <View style={styles.form2DeclarationSignature}>
            <Text style={commonStyles.textBlack}>{fullName}</Text>
            <Text>Signature or Thumb Impression of the Subscriber</Text>
          </View>
        </View>

        <Text>Strike out whichever is not applicable</Text>

        <Text style={{ ...commonStyles.header, ...commonStyles.marginTop20 }}>
          CERTIFICATE BY EMPLOYER
        </Text>
        <Text>
          Certified that the above Declaration and Nomination has been
          signed/thumb Impressed before me by {"Shri/Smt./Kum. "}
          <Text style={commonStyles.fontBold}>Mock Employee Name</Text> employed
          in my establishment after he/she has read the entries have been read
          over to him/her by me and got confirmed by him/her.
        </Text>

        <View style={commonStyles.flexRowSpaceBetween}>
          <View style={commonStyles.declarationPlaceDateStyles}>
            <Text>
              Place :{getDeclarationPlace(declarationPlace)}
            </Text>
          </View>
          <View style={commonStyles.declarationSignature}>
            <Text>{fullName}</Text>
            <Text style={commonStyles.textCenter}>
              Signature of the employer or other authorized Officers of the
              establishment
            </Text>
          </View>
        </View>
      </Page>
    );
  }
);

export default Form2;
